"use client";

import Image from "next/image";
import cn from "classnames";

import { FA } from "@/shared/components/ui/icon/FA";
import type { FontAwesomeIcon, FontAwesomeTheme } from "@/shared/components/ui/icon/fa.types";
import type { SectionCtaAttrs, IconPosition } from "./sectionCta.types";
import s from "./SectionCta.module.scss";

interface Props {
  attrs: SectionCtaAttrs;
}

const BG_CLASS: Record<string, string> = {
  gradient: s.bgGradient,
  white: s.bgWhite,
  transparent: s.bgTransparent,
};

interface CtaButtonProps {
  title: string;
  href: string;
  icon: string;
  iconTheme: string;
  iconPosition: IconPosition;
  role: "primary" | "secondary";
}

const CtaButton = ({ title, href, icon, iconTheme, iconPosition, role }: CtaButtonProps) => {
  const iconEl = icon ? (
    <FA
      icon={icon as FontAwesomeIcon}
      theme={(iconTheme || "fad") as FontAwesomeTheme}
      className={s.btnIcon}
      color={role === "secondary" ? "primary" : undefined}
    />
  ) : null;

  return (
    <a
      href={href || undefined}
      className={cn(s.btn, role === "primary" ? s.btnPrimary : s.btnSecondary)}
      target="_blank"
      rel="nofollow noopener noreferrer"
    >
      {iconPosition === "prefix" && iconEl}
      {title || (role === "primary" ? "Primary" : "Secondary")}
      {iconPosition === "suffix" && iconEl}
    </a>
  );
};

/** Media element — renders image or icon based on mediaType */
const MediaPreview = ({ attrs, large }: { attrs: SectionCtaAttrs; large?: boolean }) => {
  const isTransparent = /\.(png|webp)$/i.test(attrs.imageUrl);

  if (attrs.mediaType === "image" && attrs.imageUrl) {
    return (
      <Image
        src={attrs.imageUrl}
        alt=""
        width={attrs.imageWidth || (large ? 288 : 256)}
        height={large ? 256 : 192}
        loading="lazy"
        style={attrs.imageWidth ? { width: attrs.imageWidth, height: "auto" } : undefined}
        className={cn(large ? s.imageLarge : s.image, isTransparent ? s.imageTransparent : s.imageOpaque)}
      />
    );
  }

  if (attrs.mediaType === "icon" && attrs.icon) {
    return (
      <FA
        icon={attrs.icon as FontAwesomeIcon}
        theme={(attrs.iconTheme || "fad") as FontAwesomeTheme}
        className={cn(s.sectionIcon, large && s.sectionIconLarge)}
      />
    );
  }

  return null;
};

export const SectionCta = ({ attrs }: Props) => {
  const hasMedia =
    (attrs.mediaType === "image" && !!attrs.imageUrl) || (attrs.mediaType === "icon" && !!attrs.icon);
  const isSplit = attrs.variant === "left" || attrs.variant === "right";

  const textContent = (
    <div className={cn(s.textCol, isSplit ? s.textColSplit : s.textColCentered)}>
      {attrs.heading && <h2 className={s.heading}>{attrs.heading}</h2>}
      {attrs.subheading && <p className={s.subheading}>{attrs.subheading}</p>}
      <div className={s.buttons}>
        <CtaButton
          title={attrs.primaryTitle}
          href={attrs.primaryHref}
          icon={attrs.primaryIcon}
          iconTheme={attrs.primaryIconTheme}
          iconPosition={attrs.primaryIconPosition}
          role="primary"
        />
        {attrs.secondaryEnabled && (
          <CtaButton
            title={attrs.secondaryTitle}
            href={attrs.secondaryHref}
            icon={attrs.secondaryIcon}
            iconTheme={attrs.secondaryIconTheme}
            iconPosition={attrs.secondaryIconPosition}
            role="secondary"
          />
        )}
      </div>
    </div>
  );

  return (
    <section className={cn(s.section, BG_CLASS[attrs.background] || s.bgGradient)}>
      {isSplit ? (
        <div className={cn(s.splitContent, attrs.variant === "right" && s.splitReverse)}>
          {hasMedia && (
            <div className={s.mediaCol}>
              <MediaPreview attrs={attrs} large />
            </div>
          )}
          <div className={s.splitTextCol}>{textContent}</div>
        </div>
      ) : (
        <div className={s.centeredContent}>
          {hasMedia && <MediaPreview attrs={attrs} />}
          {textContent}
        </div>
      )}
    </section>
  );
};
